import { useToast } from "./ui/Toast";
import { StringInput } from "./StringInput";
import { getLowerCaseBaseUnit } from "./NutritionInput";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useUpdate } from "../utils/useUpdate";
import { Purchase, PurchaseCreation, PurchaseUnit } from "../data/purchase";
import { computeCalories } from "../utils/caloriesUtil";
import Food from "../data/food";
import { map, merge } from "lodash";
import {
  completePurchase,
  completePurchaseFromUrl,
  createPurchase,
  scanPurchaseLabel,
  updatePurchase,
} from "../api";
import { useUnloadAlert } from "./useUnloadAlert";
import { deepEquals } from "../utils/equalUtils";
import { BarcodeScanner } from "./BarcodeScanner";
import { showValidationMessage } from "./showValidationMessage";
import { DrawerHistoryContext } from "../contexts";
import { usePlan2PlateDisclosure } from "./Plan2PlateDrawer";
import { AiSourceSelectionDrawer } from "./AiSourceSelectionDrawer";
import { AiUrlImportDrawer } from "./AiUrlImportDrawer";
import {
  AvailabilityDrawer,
  useAvailabilityDrawer,
} from "./AvailabilityDrawer";
import { AvailabilityWithEditedFlags, isDeleted } from "../utils/editUtils";
import { useShops } from "../queries/queries";
import { Button } from "./ui/Button";
import { Checkbox } from "./ui/Checkbox";
import { Input, NumberInput } from "./ui/Input";
import { Card } from "./ui/Card";
import { SectionLabel } from "./ui/SectionLabel";
import { AddDashedButton } from "./ui/AddDashedButton";
import { Glyph, SmallIconButton } from "./SmallIconButton";
import { AvailabilityCard } from "./AvailabilityCard";
import { PurchaseNutritionCard } from "./purchase/PurchaseNutritionCard";
import { wrapSave } from "./buttons/SaveButton";

export type PurchaseEditorProps = {
  initialPurchase: Purchase;
  isCreate: boolean;
  food: Food;
  afterSave?: (createdPurchase: Purchase) => void;
};

function hasDefaultUnit(editedPurchase: Purchase | undefined) {
  return editedPurchase?.purchaseUnits.find((pu) => pu.isDefault) != undefined;
}

export const PurchaseEditor = ({
  initialPurchase,
  isCreate,
  afterSave,
  food,
}: PurchaseEditorProps) => {
  const { foodId } = initialPurchase;

  const {
    editedObject: editedPurchase,
    updateEditedObjectField: updateEditedPurchaseField,
    updateEditedObject: updateEditedPurchase,
  } = useUpdate<Purchase>(initialPurchase);

  useEffect(() => {
    updateEditedPurchase(initialPurchase);
  }, [foodId]);

  const { pristine, setPristine } = useUnloadAlert();

  useEffect(() => {
    setPristine(deepEquals(initialPurchase, editedPurchase));
  }, [editedPurchase]);

  const [nutriValuesConfirmed, setNutriValuesConfirmed] = useState(false);
  const [editingUnitIndex, setEditingUnitIndex] = useState<number | null>(null);
  const [saveIsLoading, setSaveIsLoading] = useState(false);

  const toast = useToast();
  const shops = useShops();

  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const aiSourceDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "purchase-ai-source",
  );
  const aiUrlDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "purchase-ai-url",
  );
  const [isAnalyzingUrl, setIsAnalyzingUrl] = useState(false);

  const addPurchaseUnit = () => {
    updateEditedPurchase((purchaseDraft) => {
      purchaseDraft.purchaseUnits.push({
        foodUnitId: -1,
        id: -1,
        description: "",
        netWeight: 0,
        isDefault: false,
      });
    });
    setEditingUnitIndex(editedPurchase.purchaseUnits.length);
  };

  const removePurchaseUnit = (index: number) => {
    updateEditedPurchase((purchaseDraft) => {
      purchaseDraft.purchaseUnits.splice(index, 1);
    });
    if (editingUnitIndex === index) {
      setEditingUnitIndex(null);
    } else if (editingUnitIndex !== null && editingUnitIndex > index) {
      setEditingUnitIndex(editingUnitIndex - 1);
    }
  };

  const updatePurchaseUnitField = <K extends keyof PurchaseUnit>(
    field: K,
    value: PurchaseUnit[K],
  ) => {
    if (editingUnitIndex === null) return;
    updateEditedPurchase((purchaseDraft) => {
      const purchaseUnit = purchaseDraft.purchaseUnits[editingUnitIndex];
      if (purchaseUnit) {
        purchaseUnit[field] = value;
        if (field === "isDefault" && value === true) {
          purchaseDraft.purchaseUnits.forEach((pu, idx) => {
            if (idx !== editingUnitIndex) pu.isDefault = false;
          });
        }
      }
    });
  };

  const save = async () => {
    const withInvalidDescription =
      editedPurchase.purchaseUnits.filter(isInvalidDescription);
    if (withInvalidDescription.length > 0) {
      const descriptions = map(withInvalidDescription, "description");
      toast({
        title: `Folgende Portionen haben eine zu lange Bezeichnung (>10): ${descriptions.join()}`,
        status: "error",
        isClosable: true,
      });
      return false;
    } else if (
      !hasDefaultUnit(editedPurchase) &&
      editedPurchase.purchaseRelevant
    ) {
      showValidationMessage(toast, "Keine Einkauf-Einheit definiert");
      return false;
    } else if (
      editedPurchase.purchaseRelevant &&
      editedPurchase.availabilities.length == 0
    ) {
      toast({
        title: "Keine Verfügbarkeiten definiert",
        status: "error",
        isClosable: true,
      });
      return false;
    } else {
      const purchaseCreation: PurchaseCreation = editedPurchase;
      const axiosResponse = isCreate
        ? await createPurchase({ body: purchaseCreation })
        : await updatePurchase({ body: editedPurchase });
      afterSave && afterSave(axiosResponse.data);
      return true;
    }
  };

  const doComputeCalories = () => {
    const calories = computeCalories(editedPurchase);
    updateEditedPurchaseField("kcal", calories);
  };

  const unit = getLowerCaseBaseUnit(food.baseUnit);

  const availabilityDrawerProps = useAvailabilityDrawer(
    async (availabilities) => {
      updateEditedPurchase((purchaseDraft) => {
        purchaseDraft.availabilities = availabilities.filter(
          (a) => !isDeleted(a),
        );
      });
      return false;
    },
    false,
  );

  const editAvailabilities = () => {
    availabilityDrawerProps.editAvailabilities(
      editedPurchase.id,
      editedPurchase.availabilities,
    );
  };

  const complete = async () => {
    const result = (await completePurchase({ body: editedPurchase })).data;
    updateEditedPurchase(result);
  };

  // Werte aus einer URL ableiten (analog zum FoodEditor).
  const analyzeUrl = async (url: string) => {
    setIsAnalyzingUrl(true);
    try {
      const response = await completePurchaseFromUrl({
        body: { url, purchase: editedPurchase },
      });
      updateEditedPurchase(response.data);
      aiUrlDisclosure.onClose();
      aiSourceDisclosure.onClose();
    } catch {
      toast({
        title: "Produkt konnte nicht aus der URL geladen werden",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsAnalyzingUrl(false);
    }
  };

  // Nährwerte aus einem Foto (z. B. Nährwerttabelle) ableiten.
  const handleImageScan = async (file: File) => {
    try {
      const scanned = (await scanPurchaseLabel({ body: { file } })).data;
      const mergedPurchase = merge({}, editedPurchase, scanned);
      mergedPurchase.purchaseRelevant = true;
      updateEditedPurchase(mergedPurchase);
    } catch {
      toast({
        title: "Foto konnte nicht ausgewertet werden",
        status: "error",
        isClosable: true,
      });
    }
  };

  const availabilitySummary = useMemo(
    () =>
      (editedPurchase.availabilities || [])
        .filter((a) => !isDeleted(a as unknown as AvailabilityWithEditedFlags))
        .map((a) => ({
          shopName: shops.find((s) => s.id === a.shopId)?.description ?? "",
          department: a.department
            ? {
                name: a.department.name,
                description: a.department.description,
              }
            : undefined,
        }))
        .filter((e) => e.shopName),
    [editedPurchase.availabilities, shops],
  );

  const handleUnitQuickSelect = (type: string) => {
    if (editingUnitIndex === null) return;
    let description = "";
    let weight = 0;
    switch (type) {
      case "1unit":
        description = unit;
        weight = 1;
        break;
      case "Stück":
        description = "Stück";
        break;
      case "Scheibe":
        description = "Scheibe";
        break;
      case "Portion":
        description = "Portion";
        break;
      case "Handvoll":
        description = "Handvoll";
        break;
    }
    updateEditedPurchase((purchaseDraft) => {
      const purchaseUnit = purchaseDraft.purchaseUnits[editingUnitIndex];
      if (purchaseUnit) {
        purchaseUnit.description = description;
        purchaseUnit.netWeight = weight;
      }
    });
  };

  const handleUnitSave = () => {
    if (editingUnitIndex === null) return;
    const purchaseUnit = editedPurchase.purchaseUnits[editingUnitIndex];
    if (!purchaseUnit.description.trim()) {
      showValidationMessage(toast, "Die Bezeichnung darf nicht leer sein.");
      return;
    }
    if (purchaseUnit.description.length > 10) {
      showValidationMessage(
        toast,
        "Die Bezeichnung darf maximal 10 Zeichen lang sein.",
      );
      return;
    }
    setEditingUnitIndex(null);
  };

  return (
    <>
      <AvailabilityDrawer {...availabilityDrawerProps} />
      <AiSourceSelectionDrawer
        open={aiSourceDisclosure.isOpen}
        onClose={aiSourceDisclosure.onClose}
        onSelectDescription={() => {
          aiSourceDisclosure.onClose();
          complete();
        }}
        onSelectUrl={() => {
          aiUrlDisclosure.onOpen();
        }}
        onSelectImage={handleImageScan}
        showDescriptionOption={!!editedPurchase.description?.trim()}
      />
      <AiUrlImportDrawer
        open={aiUrlDisclosure.isOpen}
        onClose={aiUrlDisclosure.onClose}
        onAnalyze={analyzeUrl}
        isLoading={isAnalyzingUrl}
      />
      <div className="flex w-full flex-col gap-5 pb-4">
        {/* Bezeichnung / Marke / EAN */}
        <Card className="p-3">
          <div className="flex w-full items-center gap-2">
            <StringInput
              fieldName="description"
              width={"100%"}
              value={editedPurchase.description}
              updater={updateEditedPurchaseField}
              placeholder="Bezeichnung"
              dataCy={"purchase-description-input"}
            />
            <div className="flex items-center h-[40px] self-end">
              <SmallIconButton
                data-cy="purchase-ai-wand-button"
                icon="magicWandSparkles"
                label="Nährwerte ableiten"
                onClick={() => aiSourceDisclosure.onOpen()}
              />
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-3">
            {!editedPurchase.isDefault && (
              <StringInput
                fieldName="brand"
                width={"100%"}
                value={editedPurchase.brand}
                updater={updateEditedPurchaseField}
                placeholder="Marke"
                dataCy={"purchase-brand-input"}
              />
            )}
            <div className="flex w-full items-center gap-2">
              <div className="flex-1">
                <NumberInput
                  placeholder="EAN"
                  value={editedPurchase.ean}
                  onValueChange={(ean) => updateEditedPurchaseField("ean", ean)}
                />
              </div>
              <div className="flex items-center h-[40px] self-end">
                <BarcodeScanner
                  onBarcodeScan={(ean) => {
                    if (!ean) {
                      showValidationMessage(toast, "EAN wurde nicht erkannt");
                      return;
                    }
                    return updateEditedPurchaseField("ean", ean);
                  }}
                />
              </div>
            </div>
            {isCreate && (
              <label className="flex cursor-pointer items-center gap-3 rounded-card border border-line bg-surface p-2.5">
                <Checkbox
                  data-cy="purchase-relevant-checkbox"
                  disabled={!isCreate}
                  checked={editedPurchase.purchaseRelevant}
                  onCheckedChange={(c) => {
                    updateEditedPurchaseField("purchaseRelevant", c);
                  }}
                />
                <span className="text-sm font-medium text-ink">
                  Einkaufsliste-Relevant
                </span>
              </label>
            )}
          </div>
        </Card>

        {/* Nährwerte */}
        <PurchaseNutritionCard
          purchase={editedPurchase}
          baseline={`pro 100${unit}`}
          onFieldChange={updateEditedPurchaseField}
          onComputeCalories={doComputeCalories}
        />

        {isCreate && !nutriValuesConfirmed && (
          <Button
            variant="tertiary"
            dataCy="purchase-confirm-nutri-button"
            onClick={() => setNutriValuesConfirmed(true)}
          >
            Nährwerte bestätigen
          </Button>
        )}

        {(!isCreate || nutriValuesConfirmed) && (
          <>
            {/* Einheiten */}
            <Card className="flex flex-col gap-4 p-4 border-line bg-surface-card">
              <div className="flex flex-col">
                <SectionLabel>Einheiten</SectionLabel>
                <span className="text-xs text-ink-muted">
                  In welchen Einheiten wird das Produkt verwendet?
                </span>
              </div>

              {!!editedPurchase.purchaseUnits.length && (
                <div className="flex flex-wrap gap-2 items-center">
                  {editedPurchase.purchaseUnits.map((purchaseUnit, idx) => {
                    const isEditing = editingUnitIndex === idx;
                    const displayLabel = purchaseUnit.description
                      ? purchaseUnit.description
                      : "Einheit...";
                    return (
                      <div
                        key={idx}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold transition ${
                          isEditing
                            ? "border-gold bg-gold/10 text-gold"
                            : "border-line bg-surface text-ink"
                        }`}
                      >
                        <span
                          className={
                            editedPurchase.isDefault
                              ? "select-none"
                              : "cursor-pointer select-none"
                          }
                          onClick={() =>
                            !editedPurchase.isDefault &&
                            setEditingUnitIndex(idx)
                          }
                        >
                          {displayLabel}
                        </span>
                        {purchaseUnit.isDefault &&
                          editedPurchase.purchaseRelevant && (
                            <span className="px-1.5 py-0.5 bg-teal/15 text-teal text-2xs font-bold rounded-xs">
                              EK
                            </span>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}

              {editingUnitIndex !== null && !editedPurchase.isDefault && (
                <div className="border border-gold rounded-card p-4 flex flex-col gap-4 mt-2 bg-surface/20">
                  <div className="flex flex-wrap gap-2 mt-1">
                    {[
                      `1${unit}`,
                      "Stück",
                      "Scheibe",
                      "Portion",
                      "Handvoll",
                    ].map((opt) => {
                      const valueMap: Record<string, string> = {
                        "1g": "1unit",
                        "1ml": "1unit",
                      };
                      const typeKey = valueMap[opt] || opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleUnitQuickSelect(typeKey)}
                          className="px-3 py-1.5 rounded-full border border-line bg-surface text-xs font-semibold text-ink-muted hover:bg-surface-card transition select-none cursor-pointer"
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-ink-muted">
                      Bezeichnung
                    </label>
                    <Input
                      data-cy="purchase-unit-description-input"
                      placeholder="z.B. Packung, Riegel"
                      value={
                        editedPurchase.purchaseUnits[editingUnitIndex]
                          .description || ""
                      }
                      onChange={(e) =>
                        updatePurchaseUnitField("description", e.target.value)
                      }
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-ink-muted">
                      Netto ({unit})
                    </label>
                    <NumberInput
                      data-cy="purchase-unit-net-weight-input"
                      value={
                        editedPurchase.purchaseUnits[editingUnitIndex].netWeight
                      }
                      onValueChange={(val) =>
                        updatePurchaseUnitField("netWeight", val || 0)
                      }
                    />
                  </div>

                  {editedPurchase.purchaseRelevant && (
                    <label className="flex items-center gap-3 cursor-pointer select-none mt-1">
                      <Checkbox
                        checked={
                          editedPurchase.purchaseUnits[editingUnitIndex]
                            .isDefault
                        }
                        onCheckedChange={(checked) =>
                          updatePurchaseUnitField("isDefault", checked)
                        }
                      />
                      <span className="text-sm text-ink">
                        Einkauf-Einheit (für Einkaufsliste)
                      </span>
                    </label>
                  )}

                  <div className="flex justify-center gap-3 border-t border-line/50 pt-3 mt-1">
                    <SmallIconButton
                      label="Einheit löschen"
                      icon="delete"
                      variant="destructive"
                      onClick={() => removePurchaseUnit(editingUnitIndex)}
                    />
                    <SmallIconButton
                      label="Einheit speichern"
                      icon="check"
                      data-cy="purchase-unit-save-button"
                      className={"!bg-teal [&_polyline]:!stroke-white"}
                      onClick={handleUnitSave}
                    />
                  </div>
                </div>
              )}

              {!editedPurchase.isDefault && (
                <AddDashedButton
                  dataCy="purchase-add-unit-button"
                  onClick={addPurchaseUnit}
                >
                  <Glyph icon="plus" size={15} />
                  <span>Einheit hinzufügen</span>
                </AddDashedButton>
              )}
            </Card>

            {/* Verfügbarkeit */}
            {editedPurchase.purchaseRelevant && (
              <AvailabilityCard
                entries={availabilitySummary}
                editDataCy="availability-button"
                onClick={editAvailabilities}
              />
            )}

            {/* Speichern */}
            <div className="mt-4">
              <Button
                fullWidth
                variant="primary"
                size="lg"
                dataCy="save-button"
                disabled={saveIsLoading || pristine}
                onClick={() => wrapSave(save, toast, setSaveIsLoading)}
                className="bg-teal hover:brightness-110 text-white font-bold text-base flex items-center justify-center gap-2"
              >
                ✓ Produkt speichern
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

function isInvalidDescription(editedUnit: PurchaseUnit) {
  return editedUnit.description.length > 10;
}
